/**
 * Cleans the gateway response data, extracting relevant information from a Uint8Array representation.
 *
 * @param {Uint8Array | undefined} data - The Uint8Array representing the gateway response data, or undefined if no data is provided.
 *
 * @return {string} The cleaned text extracted from the response data, or an empty string if no data is provided or if an error occurs during processing.
 */
export default function cleanGatewayResponse(data: Uint8Array | undefined): {
  chunk: string;
  citations: string[];
} {
  if (!data) {
    return { chunk: "", citations: [] };
  }
  
  let decoded = "";
  try {
    decoded = new TextDecoder().decode(data);
  } catch (decodeError) {
    console.error("Failed to decode data:", decodeError);
    return { chunk: "", citations: [] };
  }
  
  if (decoded.trim() === "") {
    // Skip empty chunks
    return { chunk: "", citations: [] };
  }
  
  // Log the first few characters for debugging (limit to avoid excessive logging)
  if (decoded.length > 0) {
    console.log(`Processing chunk (first 30 chars): ${decoded.substring(0, 30)}...`);
  }
  
  let text = "";
  let citations: string[] = [];
  
  // attempt to parse image data, if this fails it's not an image
  try {
    const image = JSON.parse(decoded);
    if (image?.data !== undefined && image.data[0] !== undefined) {
      return {
        chunk: `![${image.data[0].revised_prompt}](${image.data[0].url})`,
        citations: [],
      };
    }
  } catch (error) {
    // Not an image, continue with normal processing
  }
  
  // Split by newlines and process each line
  const lines = decoded.split("\n");
  for (let line of lines) {
    try {
      line = line.trim();
      if (line === "" || line === "[DONE]") continue;
      
      // Remove the "data: " prefix if present
      if (line.startsWith("data: ")) {
        line = line.substring(6);
      }
      
      // Fix malformed JSON - sometimes the start gets truncated
      if (line && !line.startsWith("{")) {
        // If we find the pattern :"..., it's likely a truncated ID field
        const idMatch = line.match(/([^{]*)"(.+?)","created":/);
        if (idMatch) {
          line = `{"id":"${idMatch[2]}","created":${line.substring(idMatch[0].length)}`;
        } else if (line.includes('{"delta":')) {
          // Handle partial chunk with "delta" field but missing prefix
          line = `{"choices":[${line}]}`;
        } else if (line.includes('"content":')) {
          // Handle case where we just have content without proper structure
          line = `{"choices":[{"delta":${line}}]}`;
        } else {
          // Otherwise, skip this line if we can't fix it
          console.warn("Skipping malformed line:", line.substring(0, 50));
          continue;
        }
      }
      
      // Try to parse the JSON
      let parsed;
      try {
        parsed = JSON.parse(line);
      } catch (parseError) {
        // If initial parsing fails, try additional corrections for common issues
        if (line.endsWith('"}')) {
          // Sometimes the JSON has unnecessary trailing characters
          try {
            const fixedLine = line.replace(/"}[^"]*$/, '"}');
            parsed = JSON.parse(fixedLine);
          } catch (fixError) {
            console.warn("Failed to fix truncated JSON:", line.substring(0, 50));
            continue;
          }
        } else {
          console.warn("Unparseable JSON:", line.substring(0, 50));
          continue;
        }
      }
      
      // Extract content from the parsed response
      if (parsed?.choices?.[0]?.delta?.content) {
        text = text.concat(parsed.choices[0].delta.content);
      } else if (parsed?.choices?.[0]?.message?.content) {
        // Alternative format sometimes used
        text = text.concat(parsed.choices[0].message.content);
      }
      
      // Extract citations if available
      if (parsed.citations) {
        citations = parsed.citations;
      }
    } catch (error) {
      console.error(
        `Error parsing streaming data: ${error}. Raw data: ${line.substring(0, 100)}...`,
      );
      // Continue processing remaining lines even if this one failed
    }
  }
  
  return { chunk: text, citations: citations };
}
